import React, { FC, useEffect, useState, useRef } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import TopBlock from './TopBlock';
import { RootStoreType } from 'src/types/store';
import {
    allColumns,
    allColumnsWithProvider,
    REQUIRED_PRODUCTS_GRID_ALL_PROVIDERS_COLUMNS,
    REQUIRED_PRODUCTS_GRID_COLUMNS,
} from './constants';
import Table from 'src/components/Table';
import {
    TablePropsOptionsType,
    OnChangeOptionsType,
    ExportTypeType,
    TableRefType,
    GetAllPrintRowsType,
    TableRowType,
    GridFilterType,
    RowActionPropsType, SelectRowType, CustomRenderType, TableColumnType
} from 'src/types/table';
import { InfoIcon, InventoryIcon, TransportIcon } from 'src/components/SkladonIcon/Icons';
import { useDebounce } from 'src/utils/debounce';
import { editUserSettingsFetch } from 'src/resources/userSettings/reducer';
import { UserSettingsType } from 'src/types/userSettings';
import filterUnique from 'src/utils/filterUnique';
import api from '@api';
import { ProductType } from 'src/types/product';
import { useCheckPermission } from 'src/utils/checkPermission';
import checkOrChangeProvider from 'src/utils/checkOrChangeProvider';
import { getProductsGridFetch } from 'src/resources/productsGrid/reducer';
import { PRODUCT_GRID_ALL_PROVIDERS_URL, PRODUCT_GRID_URL } from 'src/resources/productsGrid/sagas';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { disabbleMessages } from 'src/resources/alertMessages/reducer';
import {
    activateProductsFetch,
    deactivateProductsFetch, syncEansToDcixFetch,
    syncProductsToDcixFetch
} from '../../resources/productsList/reducer';
import { patchMultipleProductsFetch } from '../../resources/productsUpdate/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from '../../components/ConfirmDialog';
import MassEditDialog, { MassEditDialogPropsType } from '../ProductsListContainer/MassEditDialog';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Tooltip from 'src/components/Tooltip';
import { colors } from '../../themes/default';
import { useLocation } from 'react-router-dom';
import { productTableColumns } from '../OrdersImportContainer/constants';
import { createEanLabelUrl, getActiveEansFromProduct } from '../../utils/createEanLink';
import { TableLink, TableRouterLink } from 'src/components/TableLink';
import {
    RECEIVING_GRID_ALL_PROVIDERS_URL,
    RECEIVING_GRID_PRODUCTS_URL,
    RECEIVING_GRID_URL
} from '../../resources/receivingsGrid/sagas';
import { extendProductColumns } from '../AllReceivingsContainer/constants';
import { countryOptionsCodes } from '../../utils/countryOptions';
import { getProductSourceSysSelectOptions } from '../../utils/productSourceSysSelectOptions';

const InfoIconStyled = styled('span')({
    marginLeft: '6px',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    '& svg': {
        width: '14px',
        height: '14px',
    }
});

export const SESSION_NAME = 'productsTable';

const initTableOptions: TablePropsOptionsType = {
    filters: [],
    sort: { column: 'ID', direction: 'DESC' },
    page: 1,
    items: 20,
    columns: [],
};

const dcixColumns = [
    'ALLOCATED_QUANTITY',
    'HOLD_QUANTITY',
    'AVAILABLE_QUANTITY',
    'QUALITY_HOLD_QUANTITY',
    'DUTY_QUANTITY',
    'EXPECTED_QUANTITY',
    'QUANTITY',
    'lot',
    'expirationDate'
];
const AllProductsContainer: FC<{ filter: string, allProviders: boolean }> = (props) => {
    const dispatch = useDispatch();
    const tableRef = useRef<TableRefType>(null);

    const sessionOptions = JSON.parse(sessionStorage.getItem(SESSION_NAME) || 'null');

    const [propsFilter, setPropsFilter] = useState<GridFilterType[] | undefined>(undefined);
    const [allProviders, setAllProviders] = useState<boolean>(props.allProviders);
    const [formatedData, setFormatedData] = useState<ProductType[]>([]);
    const [tableOptions, setTableOptions] = useState<TablePropsOptionsType>(sessionOptions || initTableOptions);
    const [selectedRows, setSelectedRows] = useState<SelectRowType[]>([]);
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const [massEditParams, setMassEditParams] = useState<Partial<MassEditDialogPropsType> | null>(null);
    const loading = useSelector((state: RootStoreType) => state.productsGrid.loading);
    const productsData = useSelector((state: RootStoreType) => state.productsGrid.data);

    const productInsetTypeCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.productInsetTypeCodes
    );
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const insertByOrderTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.insertByOrderTypeCodes);
    const scanImeiSnCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.scanImeiSnCodes);
    const productTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.productTypeCodes);
    const expeditionMethodCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.expeditionMethodCodes);
    const currencyCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.currencyCodes);
    const batteryTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.batteryTypeCodes);
    const userSettingsData = useSelector((state: RootStoreType) => state.userSettings.data);

    const providers = useSelector((state: RootStoreType) => state.user.data?.providers);
    const provider = useSelector((state: RootStoreType) => state.provider.data);
    const stockRation = useSelector((state: RootStoreType) => state.provider.data?.SHOPTET_STOCK_RATIO);

    const stockRatioPercent = Math.round((stockRation || 0) * 100);
    const stockRatioColor = stockRatioPercent === 0
        ? colors.alert_O
        : stockRatioPercent < 100
            ? colors.warning_O
            : colors.text_T2;

    const permissionCreate = useCheckPermission('PRODUCT.CREATE');
    const permissionDetail = useCheckPermission('PRODUCT.DETAIL');
    const permissionUpdate = useCheckPermission('PRODUCT.UPDATE');
    const permissionAllProviders = useCheckPermission('PRODUCT.LISTALLPROVIDERS');
    const permissionDcixImportProducts = useCheckPermission('PRODUCT.ACTION.DCIX.IMPORT.PRODUCTS');
    const permissionDcixImportEans = useCheckPermission('PRODUCT.ACTION.DCIX.IMPORT.EANS');
    const permissionGlobalProduct = useCheckPermission('PRODUCT.GLOBAL_PRODUCT');
    const permissionQuantityConversion = useCheckPermission('PRODUCT.QUANTITY_CONVERSION');
    useEffect(() => {
        let newPropsFilter: GridFilterType[] | undefined = undefined;
        switch (props.filter) {
            case 'all':
                newPropsFilter = [];
                break;
            case 'packagingMaterial':
                newPropsFilter = [{ column: 'PRODUCT_TYPE', operator: 'in', value: ['packagingMaterial', 'pallet'] }];
                break;
            case 'promoMaterial':
                newPropsFilter = [{ column: 'PRODUCT_TYPE', operator: 'eq', value: 'promoMaterial' }];
                break;
            case 'inactive':
                newPropsFilter = [
                    { column: 'ACTIVE', operator: 'eq', value: '0' },
                    { column: 'IS_DELETED', operator: 'eq', value: '0' },
                    { column: 'PRODUCT_TYPE', operator: 'nin', value: ['packagingMaterial', 'promoMaterial', 'pallet'] },
                ];
                break;
            case 'deleted':
                newPropsFilter = [
                    { column: 'IS_DELETED', operator: 'eq', value: '1' },
                    { column: 'PRODUCT_TYPE', operator: 'nin', value: ['packagingMaterial', 'promoMaterial', 'pallet'] },
                ];
                break;
            case 'active':
            default:
                newPropsFilter = [
                    { column: 'ACTIVE', operator: 'eq', value: '1' },
                    { column: 'IS_DELETED', operator: 'eq', value: '0' },
                    { column: 'PRODUCT_TYPE', operator: 'nin', value: ['packagingMaterial', 'promoMaterial', 'pallet'] },
                ];

                break;
        }
        setPropsFilter(newPropsFilter);
    }, [props.filter]);

    const location = useLocation();

    useEffect(() => {
        setAllProviders(props.allProviders);
    }, [location.pathname]);

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('products.title') }));
    }, []);

    const debouncedTableOptions = useDebounce(tableOptions, 500);

    useEffect(() => {
        const columns = debouncedTableOptions.columns || [];
        // @ts-ignore
        const includesDcixColumns = columns.some((column) => dcixColumns.includes(column.key));
        if ((props.filter && !propsFilter) || loading || includesDcixColumns) {
            return;
        }
        if (propsFilter) {
            loadTableData();
        }
    }, [debouncedTableOptions, propsFilter, allProviders]);

    useEffect(() => {
        setFormatedData([]);
        setSelectedRows([]);
    }, [allProviders]);

    useEffect(() => {
        if (productsData?.data) {
            setFormatedData(getFormatedData(productsData?.data || []));
        }
    }, [productsData?.data, allProviders, providers]);

    const buildGridRowId = (item: ProductType): string =>
        `${item.ID}-${item.OWNER_ESHOP_KEY ?? ''}`;

    const getFormatedData = (data: ProductType[]): ProductType[] => {
        return data.map((item) => {
            const gridRowId = buildGridRowId(item);
            if (!allProviders) {
                return { ...item, gridRowId };
            }
            const provider = providers?.find((provider) => provider.ESHOP_KEY === item.OWNER_ESHOP_KEY);
            return { ...item, gridRowId, providerName: provider?.COMPANY_NAME };
        });
    };

    const getAllGridParams = () => {
        const filters = [...(propsFilter || []), ...(tableOptions.filters || [])];
        return ({
            // unfilter fitlers for columns that are not in DB
            filters: [filters.filter((filter) => !dcixColumns.includes(filter.column))],
            // unfilter sorts for columns that are not in DB
            sorts: [dcixColumns.includes(tableOptions.sort.column) ? initTableOptions.sort : tableOptions.sort],
            page: tableOptions.page,
            items: tableOptions.items,
            columns: [
                ...(allProviders ? REQUIRED_PRODUCTS_GRID_ALL_PROVIDERS_COLUMNS : REQUIRED_PRODUCTS_GRID_COLUMNS),
                ...tableOptions.columns.filter((column) => {
                    if (column.key === 'QUANTITY_CONVERSION' && !permissionQuantityConversion) return false;
                    if (column.key === 'GLOBAL_PRODUCT' && !permissionGlobalProduct) return false;
                    return !column.allwaysShow;
                }).map((column) => column.key)
            ].filter(filterUnique)
        });
    };

    const loadTableData = () => {
        if (tableOptions.columns.length) {
            dispatch(getProductsGridFetch({ params: getAllGridParams(), allProviders }));
        }
    };


    const handleActivateProducts = () => {
        setConfirmParams({
            title: translate('products.activateProducts'),
            content: translate('products.activateProductsConfirm', { count: selectedRows.length }),
            action: () => {
                dispatch(activateProductsFetch(selectedRows));
                setSelectedRows([]);
            }
        });
    };
    const handleDeactivateProducts = () => {
        setConfirmParams({
            title: translate('products.deactivateProducts'),
            content: translate('products.deactivateProductsConfirm', { count: selectedRows.length }),
            action: () => {
                dispatch(deactivateProductsFetch(selectedRows));
                setSelectedRows([]);
            }
        });
    };

    const handleMassUpdate = () => {
        setMassEditParams({
            title: translate('products.massUpdate'),
            content: translate('products.massUpdateConfirm',    { count: selectedRows.length }),
            action: (data) => {
                dispatch(patchMultipleProductsFetch({
                    products: productsData?.data?.filter((product) => selectedRows.includes(String(product.ID))),
                    data,
                    params: getAllGridParams()
                }));
                setSelectedRows([]);
            }
        });
    };

    const handleChangeOptions: OnChangeOptionsType = (newOptions) => {
        setTableOptions(newOptions);
    };

    const handleExport = (type: ExportTypeType) => {
        tableRef.current?.exportTableGrid(
            type,
            translate('products.action.exportFileName'),
            getAllGridParams,
            allProviders ? PRODUCT_GRID_ALL_PROVIDERS_URL : PRODUCT_GRID_URL,
        );
    };

    const editUserSettings = (newUserSettings: UserSettingsType) => {
        dispatch(editUserSettingsFetch(newUserSettings));
    };

    const changeProvider = (row: TableRowType) => {
        if (allProviders && providers && provider) {
            if (checkOrChangeProvider(providers, provider, 'ESHOP_KEY', row.OWNER_ESHOP_KEY)) {
                dispatch(disabbleMessages());
            }
        }
    };

    const handleAllProviders = (value: boolean) => {
        setAllProviders(value);
    };

    const handleSyncProductsToDcixFetch = () => {
        dispatch(syncProductsToDcixFetch());
    };
    const handleSyncEansToDcixFetch = () => {
        dispatch(syncEansToDcixFetch());
    };

    const reloadTableData = () => {
        dispatch(getProductsGridFetch(undefined));
    };

    const customRender: CustomRenderType = (column, row) => {
        switch (column.key) {
            case 'NAME':
                return (
                    <TableRouterLink to={`/products/${row.ID}`}>
                        {row[column.key]}
                    </TableRouterLink>
                );
            case 'EAN': {
                const eans = getActiveEansFromProduct(row);
                if (!eans.length) {
                    return row[column.key] || '';
                }
                return (
                    <>
                        {eans.map((ean) => (
                            <div key={ean}>
                                <TableLink
                                    href={createEanLabelUrl(String(row.SKU || ''), ean)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {ean}
                                </TableLink>
                            </div>
                        ))}
                    </>
                );
            }
        }
    };

    const tableRowActions: RowActionPropsType[] = [
        {
            icon: <InventoryIcon />,
            label: translate('products.action.productDetail'),
            getLink: (row) => `/products/${row.ID}/`,
            onClick: changeProvider,
            hiddenFce: () => !permissionDetail,
        },
        // {
        //     icon: <TransportIcon />,
        //     label: translate('products.action.stockTransfers'),
        //     getLink: (row) => `/products/stockTransfers/${row.ID}/`,
        //     hiddenFce: () => !permissionDetail
        // }
    ];

    const tableSelectOptions: {
        [key: string]: ConstantsCodeType[];
    } = {
        productInsetTypeCodes,
        boolNumberCodes,
        scanImeiSnCodes,
        productTypeCodes,
        expeditionMethodCodes,
        currencyCodes,
        countryOptionsCodes,
        insertByOrderTypeCodes,
        batteryTypeCodes,
        productSourceSysCodes: getProductSourceSysSelectOptions(),
    };

    const tableColumns: TableColumnType[] = allProviders ? allColumnsWithProvider : allColumns;

    return (
        <>
            <TopBlock
                handleExport={handleExport}
                allProviders={allProviders}
                setAllProviders={handleAllProviders}
                reloadTableData={reloadTableData}
                permissionCreate={permissionCreate}
                permissionAllProviders={permissionAllProviders}
                permissionDcixImportProducts={permissionDcixImportProducts}
                permissionDcixImportEans={permissionDcixImportEans}
                permissionUpdate={permissionUpdate}
                handleActivateProducts={handleActivateProducts}
                handleDeactivateProducts={handleDeactivateProducts}
                handleMassUpdate={handleMassUpdate}
                selectedRows={selectedRows}
                filter={props.filter}
                productsData={productsData?.data || []}
                reloadProductsList={loadTableData}
                handleSyncProductsToDcixFetch={handleSyncProductsToDcixFetch}
                handleSyncEansToDcixFetch={handleSyncEansToDcixFetch}

            />
            <Table
                allColumns={tableColumns}
                ref={tableRef}
                rowIdKey="gridRowId"
                selectRowIdKey="ID"
                rows={formatedData}
                loading={loading}
                columsLabelPrefix="products.table"
                options={tableOptions}
                totalCount={productsData?.totalCount}
                onChangeOptions={handleChangeOptions}
                rowActions={tableRowActions}
                selectOptions={tableSelectOptions}
                userSettingsName="showProductsTableColumns"
                userSettingsData={userSettingsData}
                editUserSettings={editUserSettings}
                selectedRows={!allProviders ? selectedRows : undefined}
                setSelectedRows={!allProviders ? setSelectedRows : undefined}
                customRender={customRender}
                optionsSessionName={SESSION_NAME}
                heightVariant="top-tab"
                fullHeight
                rowSxFce={(row) => {
                    if (row.IS_DELETED === 1) {
                        return { color: colors.alert_O, '& .MuiTableCell-root': { color: colors.alert_O } };
                    }
                    if (row.ACTIVE === 0) {
                        return { color: colors.text_T3, '& .MuiTableCell-root': { color: colors.text_T3 } };
                    }
                    return { color: colors.text_T0, '& .MuiTableCell-root': { color: colors.text_T0 } };
                }}
                footerLeft={
                    <>
                        <Typography variant="body2" sx={{ color: stockRatioColor }}>
                            {translate('products.stockRatio.label')}: {stockRatioPercent}%
                        </Typography>
                        <Tooltip title={translate('products.stockRatio.tooltip')}>
                            <InfoIconStyled>
                                <InfoIcon />
                            </InfoIconStyled>
                        </Tooltip>
                    </>
                }
            />
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={(): any => setConfirmParams(null)}
                />
            )}
            {massEditParams && (
                <MassEditDialog
                    title={massEditParams.title || ''}
                    content={massEditParams.content}
                    action={massEditParams.action}
                    onClose={(): any => setMassEditParams(null)}
                    productTypeCodes={productTypeCodes}
                    expeditionMethodCodes={expeditionMethodCodes}
                    boolNumberCodes={boolNumberCodes}
                    scanImeiSnCodes={scanImeiSnCodes}
                    currencyCodes={currencyCodes}
                    batteryTypeCodes={batteryTypeCodes}
                />
            )}
        </>
    );
};

export default AllProductsContainer;
